<template>
  <div class="project-bar bar px-2 d-flex "  >
    <v-col class="ma-auto pa-0">
      <v-row class="pa-0 ma-0 d-flex">
        <v-col class="pa-0 my-auto title-container">
          <v-row class="ma-0 full-width">
            <CustomTextInput v-model="newName" class="mr-0 " :token="'token-text-medium'"  :plain="true" @blur="doNameChange" @keypress.enter="doNameChange" :max="30"/>
          </v-row>
        </v-col>
        <template v-if="currentClusterId">
          <Divider :vertical="true" class="mx-2"/>
          <v-col  class="pa-0 fit-content d-flex-col">
            <v-row class="ma-0 token-text-medium price token-text-color-primary ml-2" v-if="monthlyPrice">
              <PriceDisplay :price="displayPriceMin" :price2="displayPriceMax" :currency="customer?.attributes.currency"/>
            </v-row>
            <v-row class="ma-0 token-text-medium price token-text-color-primary ma-auto" v-else>
              <Spinner class="ma-auto"/>
            </v-row>
          </v-col>
        </template>
      </v-row>
    </v-col>
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import Logo from '@/components/UIElements/Logo.vue'
import Badge from '@/components/UIElements/Badge.vue'
import Divider from '@/components/UIElements/Divider.vue'
import CustomTextInput from '@/components/UIElements/CustomTextInput.vue'
import PriceDisplay from '@/components/UIElements/PriceDisplay.vue'
import Spinner from '@/components/UIElements/Spinner.vue'

import { Watch } from 'vue-property-decorator'
import API from '@/api/wrapper'
import { APIPriceRanges, APIClusterNodeTypologie, APICluster, APICustomer, APIClusterRequirement, APILoadBalancerTypologie } from '@/typesAPI'
import { NodePool } from '@/types'

@Options({
  components: {
    Logo,
    Badge,
    Divider,
    CustomTextInput,
    PriceDisplay,
    Spinner
  },
})
export default class ProjectBar extends Vue {

  showModalRenameProject = false
  newName = ""

  get currentProject() {
    return this.$store.getters['projects/getCurrentProject']
  }

  @Watch('currentProject', {immediate: true, deep: true})
  onCurrentProjectChange() {
    if(this.currentProject) {
      this.newName = this.currentProject?.attributes?.name
    }
  }

  doNameChange() {
    if(this.newName !== this.currentProject.attributes.name) {
      API.projects.edit(this.currentProject.id, {name:this.newName})
      .then((newVal) => {
        this.$store.dispatch('projects/updateProject', newVal)
      }) 
    }
  }

  //Price 

  mounted(): void {
    if(!this.monthlyPrice) {
      API.prices.getMonthly(this.$route.params.projectId as string)
      .then ((res:APIPriceRanges) => {
        this.$store.dispatch('prices/setMonthlyPrice', res)
      })   
    }
  }

  get customer():APICustomer {
    return this.$store.getters['user/getCustomer']
  }
  get monthlyPrice():APIPriceRanges | undefined {
    return this.$store.getters['prices/getMonthlyPrice']
  }
  
  get providerRequirements ():APIClusterRequirement[] {
    if(!this.selectedCluster) {
      return []
    }
    return this.$store.getters['clusters/getClusterRequirementsByProviderAndLocation'](this.selectedCluster?.attributes.provider, this.selectedCluster.attributes.region)
  }

  get autoPilotMinPrice ():number {
    const workerTypologies = this.$helpers.clusters.getTypologiesFromRequirements(
      this.providerRequirements,
      this.clusterNodeTypologies,
      0,
      this.$enums.RequirementTypes.WORKER,
      this.selectedCluster?.attributes.requested_config.criticality,
    ) as APIClusterNodeTypologie[]
    return workerTypologies.reduce(
      (total, typology) => total + Number(typology.attributes.price.amount || 0),
      0,
    )
  }

  get displayPriceMin():number {
    if(this.selectedCluster && !this.selectedCluster.attributes.requested_config.node_pools.length) {
      return this.autoPilotMinPrice
    }

    const priceProject = this.monthlyPrice ? parseInt(this.monthlyPrice.attributes.amount_min) : 0
    return this.minPriceLoadBalancer + this.minPriceMasterNodes + this.minPriceWorkerNodes + priceProject
  }

  get displayPriceMax():number {
    if(this.selectedCluster && !this.selectedCluster.attributes.requested_config.node_pools.length) {
      return this.selectedCluster.attributes.requested_config.auto_pilot.max_budget ?? 0
    }

    const priceProject = this.monthlyPrice ? parseInt(this.monthlyPrice.attributes.amount_max) : 0
    return this.maxPriceLoadBalancer + this.maxPriceMasterNodes + this.maxPriceWorkerNodes + priceProject
  }


  get clusterNodeTypologies():APIClusterNodeTypologie[] {
    if(!this.selectedCluster) {
      return []
    }
    return this.$store.getters['clusters/getClusterNodeTypologiesByProviderAndLocation'](this.selectedCluster?.attributes.provider, this.selectedCluster?.attributes?.region)
  }

  get loadBalancerTypologies():APILoadBalancerTypologie[] {
    if(!this.selectedCluster) {
      return []
    }
    return this.$store.getters['clusters/getLoadBalancerTypologiesByProviderAndLocation'](this.selectedCluster?.attributes.provider, this.selectedCluster?.attributes?.region)
  }

  get clusters():APICluster[] {
    return this.$store.getters['clusters/getClusters']
  }

  get selectedCluster():APICluster | null {
    const ret = this.clusters?.length ? this.clusters.find(cluster => cluster.id === this.currentClusterId) : null
    return ret ? ret : null
  }

  get currentClusterId():string | null {
    if(this.currentProject && this.currentProject?.relationships?.kubernetes_clusters?.data?.length) {
      return this.currentProject?.relationships?.kubernetes_clusters?.data[0].id
    }
    return null
  }
  
  get nbNodes():{min:number, max:number} {
    if(!this.nodePoolList?.length) {
      return {min: 0, max: 0}
    }
    if(!this.isAutoPilot){
      const n = this.nodePoolList.reduce((accumulator, val) => accumulator + val.target_size, 0)
      return {min: n, max: n}

    } 
    return {
      min: this.nodePoolList.reduce((accumulator, val) => accumulator + val.min_size, 0),
      max: this.nodePoolList.reduce((accumulator, val) => accumulator + val.max_size, 0),
    }
  }

  
  // Nb load balancer
  get minLoadBalancer ():APILoadBalancerTypologie | null {
    const typoRequirement = this.$helpers.clusters.getTypologiesFromRequirements(this.providerRequirements, this.loadBalancerTypologies, this.nbNodes.min, this.$enums.RequirementTypes.LOAD_BALANCER) as APILoadBalancerTypologie[]
    return typoRequirement.length ? typoRequirement[0] : null
  }

  get maxLoadBalancer ():APILoadBalancerTypologie | null {
    const typoRequirement = this.$helpers.clusters.getTypologiesFromRequirements(this.providerRequirements, this.loadBalancerTypologies, this.nbNodes.max, this.$enums.RequirementTypes.LOAD_BALANCER) as APILoadBalancerTypologie[]
    return typoRequirement.length ? typoRequirement[0] : null
  }

  get isAutoPilot():boolean {
    if(!this.selectedCluster) {
      return false
    }
    return this.selectedCluster.attributes.requested_config.auto_pilot.enabled || false
  }
  get nodePoolList():NodePool[] {
    if(!this.selectedCluster) {
      return []
    }
    return this.selectedCluster.attributes.requested_config.node_pools as NodePool[] || []
  }

  get masterNodes():{min:APIClusterNodeTypologie[], max:APIClusterNodeTypologie[]} {
    if(!this.isAutoPilot) {
      return {min: [], max: []}
    }
    return {
      min: this.$helpers.clusters.getTypologiesFromRequirements(
        this.providerRequirements,
        this.clusterNodeTypologies,
        this.nbNodes.min,
        this.$enums.RequirementTypes.MASTER,
        this.selectedCluster?.attributes.requested_config.criticality,
      ) as APIClusterNodeTypologie[],
      max: this.$helpers.clusters.getTypologiesFromRequirements(
        this.providerRequirements,
        this.clusterNodeTypologies,
        this.nbNodes.max,
        this.$enums.RequirementTypes.MASTER,
        this.selectedCluster?.attributes.requested_config.criticality,
      ) as APIClusterNodeTypologie[],
    }
  }

  get minPriceLoadBalancer():number {
    let ret = 0
    if(this.minLoadBalancer) {
      ret = parseInt(this.minLoadBalancer.attributes.price?.amount)
    }
    return ret
  }

  get minPriceMasterNodes():number {
    let ret = 0
    ret = this.masterNodes.min.length ? this.masterNodes.min.length * parseInt(this.masterNodes.min[0].attributes.price?.amount) : 0
    return ret
  }

  get minPriceWorkerNodes():number {
    let ret = 0
    // If we have a node pool, we can calculate the min price based on the min size of each node pool
    this.nodePoolList.forEach((nodePool:NodePool) => {
      const typology = this.clusterNodeTypologies.find((typology:APIClusterNodeTypologie) => {
        return typology.id === nodePool.typology
      })
      if(typology) {
        ret += (this.isAutoPilot ? nodePool.min_size : nodePool.target_size) * parseInt(typology?.attributes.price?.amount || '0')
      }
    })
    
    return ret
  }

  get maxPriceLoadBalancer():number {
    if(!this.isAutoPilot) {
      return this.minPriceLoadBalancer
    }
    let ret = 0
    if(this.maxLoadBalancer) {
      ret = parseInt(this.maxLoadBalancer.attributes.price?.amount)
    }
    return ret
  }

  get maxPriceMasterNodes():number {
    if(!this.isAutoPilot) {
      return this.minPriceMasterNodes
    }
    let ret = 0
    ret = this.masterNodes.max.length ? this.masterNodes.max.length * parseInt(this.masterNodes.max[0].attributes.price?.amount) : 0
    return ret
  }

  get maxPriceWorkerNodes():number {
    if(!this.isAutoPilot) {
      return this.minPriceWorkerNodes
    }
    let ret = 0
    // If we have a node pool, we can calculate the max price based on the max size of each node pool
    this.nodePoolList.forEach((nodePool:NodePool) => {
      const typology = this.clusterNodeTypologies.find((typology:APIClusterNodeTypologie) => {
        return typology.id === nodePool.typology
      })
      if(typology) {
        ret += nodePool.max_size * parseInt(typology?.attributes.price?.amount || '0')
      }
    })
    
    return ret
  }

  get totalPriceMin():number {
    return this.minPriceWorkerNodes + this.minPriceMasterNodes + this.minPriceLoadBalancer
  }

  get totalPriceMax():number {
    return this.maxPriceWorkerNodes + this.maxPriceMasterNodes + this.maxPriceLoadBalancer
  }
}
</script>

<style lang="scss">
@import '@/css/variables';

.project-bar {
  height: 56px;
  font-size: 16px;
  font-weight: bolder;
  transition: width 0.2s ease-in-out 1.8s;

  .title-container {
    opacity: 1;
    transition: opacity 0.2s ease-in-out 2s;
    width: 142px;
    max-width: 142px;
    min-width: 142px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: block;
    :deep(.custom-text-input-container) {
      height: 38px !important;
    }
  }
}
</style>
